import Table from '@frontend/Components/Table/Table';
import type { Agent } from '@frontend/Models/Agent/AgentModels';
import AgentsService from '@frontend/AppSettings/services/AgentsService';
import { createAgentsColumns } from './agentsTableColumns';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import AgentDetails from '../AgentDetails/AgentDetails';
/**
 * AgentsTable Component
 *
 * A component that displays agent information in a tabular format.
 * Features include:
 * - Responsive table layout with clean design
 * - Agent ID links with hover effects
 * - User icons for each agent
 * - Mobile-responsive design
 *
 * This component is part of the Agents section and provides
 * a comprehensive view of agent information including:
 * - Agent ID
 * - Full Name
 * - Company
 * - Contact Information
 * - Address
 */

/**
 * AgentsTable Component
 *
 * @param {Object} props - Component props
 * @param {Array<Object>} props.data - Array of agent objects containing:
 * @param {string} props.data[].AgentID - Unique identifier for the agent
 * @param {string} props.data[].FirstName - Agent's first name
 * @param {string} props.data[].LastName - Agent's last name
 * @param {string} props.data[].Company - Agent's company name
 * @param {string} props.data[].Email - Agent's email address
 * @param {string} props.data[].Phone - Agent's phone number
 * @param {string} props.data[].Address - Agent's physical address
 * @param {Function} props.onAgentDeleted - Callback function to refresh agent list after deletion
 */

type Props = {
  data?: Agent[];
  onAgentDeleted?: () => void;
  pageSize?: number;
  pagination?: {
    currPage: number;
    onPageChange: (page: number) => void;
    pageLimit: number;
    pageSize: number;
    onPageSizeChange: (size: number) => void;
  };
};

const AgentsTable = ({ data, onAgentDeleted, pageSize, pagination }: Props) => {
  const [selectedAgentId, setSelectedAgentId] = useState<string | null>(null);
  const [showAgentDetails, setShowAgentDetails] = useState(false);

  const handleAgentClick = (agentId: string) => {
    setSelectedAgentId(agentId);
    setShowAgentDetails(true);
  };

  const handleCloseAgentDetails = () => {
    setShowAgentDetails(false);
    setSelectedAgentId(null);
  };

  const handleAgentUpdated = () => {
    // Refresh the agents list when an agent is updated
    if (onAgentDeleted) {
      onAgentDeleted();
    }
  };
  const handleDelete = async (agentId: string): Promise<string | undefined> => {
    if (!agentId) {
      console.error('Agent ID is undefined');
      throw new Error('Invalid agent ID');
    }

    try {
      const result = await AgentsService.deleteAgent(agentId);
      // Call the callback to refresh the agent list
      if (onAgentDeleted) {
        onAgentDeleted();
      }
      return result && result.message ? String(result.message) : undefined;
    } catch (error) {
      console.error('Error deleting agent:', error);
      throw error;
    }
  };

  if (!data) {
    return <></>;
  }
  const columns = createAgentsColumns(handleDelete, handleAgentClick);
  const emptyAction: React.ReactElement = (
    <Link to="/createAgent" className="text-link">
      <h3 style={{ textAlign: 'center' }}>Click to add new agent</h3>
    </Link>
  );

  return (
    <>
      <Table
        columns={columns}
        data={data}
        emptyMessage="No agents found"
        emptyAction={emptyAction as any}
        rowKey="AgentID"
        pagination={pagination}
      />
      {showAgentDetails && selectedAgentId && (
        <AgentDetails
          agentId={selectedAgentId}
          onClose={handleCloseAgentDetails}
          onAgentUpdated={handleAgentUpdated}
        />
      )}
    </>
  );
};

export default AgentsTable;
